import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { SearchResult, ImageTag } from '@amzn/fire-tv-campaign-automation-service';
import styles from './SearchResults.module.css';

export interface SearchResultsProps {
  results: SearchResult[];
  onTileClick: (result: SearchResult) => void;
  disabled: boolean;
  selectedImageUrl?: string | null;
}

export const SearchResults: FC<SearchResultsProps> = ({
  results,
  onTileClick,
  disabled,
  selectedImageUrl,
}) => {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const isSelected = useCallback(
    (result: SearchResult) => {
      if (!selectedImageUrl) return false;
      return result.images.some((img) => img.url === selectedImageUrl);
    },
    [selectedImageUrl],
  );

  const handleTileClick = useCallback(
    (result: SearchResult, index: number) => {
      setFocusedIndex(index);
      onTileClick(result);
    },
    [onTileClick],
  );

  useEffect(() => {
    setFocusedIndex(-1);
    // Autofocus first result when results are available
    if (results && results.length > 0) {
      setFocusedIndex(0);
      // Focus the container to enable keyboard navigation
      containerRef.current?.focus();
    }
  }, [results]);

  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const focusedElement = containerRef.current.children[focusedIndex];
      if (focusedElement && typeof focusedElement.scrollIntoView === 'function') {
        focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (disabled || results?.length === 0) return;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % results.length);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
          break;
        case 'Enter':
        case ' ':
          if (focusedIndex >= 0) {
            event.preventDefault();
            onTileClick(results[focusedIndex]);
          }
          break;
        case 'Escape':
          setFocusedIndex(-1);
          break;
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }
  }, [disabled, results, focusedIndex, onTileClick]);

  if (results.length === 0) return null;

  return (
    <div className={styles.resultsSection}>
      <div className={styles.resultsHeader}>
        <span className={styles.resultCount}>
          {results.length} result{results.length !== 1 ? 's' : ''} found
        </span>
        <span className={styles.resultHint}>Click on any item to view and select images</span>
      </div>
      <div
        ref={containerRef}
        className={styles.resultsContainer}
        tabIndex={disabled ? -1 : 0}
        role="grid"
        aria-label="Search results"
      >
        {results.map((result, index) => {
          const coverImage = result.images.find((img) => img.tags.includes(ImageTag.COVER_IMAGE));
          const heroImage = result.images.find((img) => img.tags.includes(ImageTag.HERO_IMAGE));
          const displayImage = coverImage || heroImage;

          const selected = isSelected(result);

          return (
            <div
              key={`${result.catalogueId}-${index}`}
              className={`${styles.resultTile} ${disabled ? styles.disabled : ''} ${focusedIndex === index ? styles.focused : ''} ${selected ? styles.selected : ''}`}
              onClick={disabled ? undefined : () => handleTileClick(result, index)}
              role="gridcell"
              aria-label={`Select images for ${result.title}`}
              aria-selected={focusedIndex === index || selected}
            >
              <img src={displayImage?.url} alt={result.title} className={styles.tileImage} />
              <div className={styles.tileContent}>
                <div className={styles.tileTitle}>{result.title}</div>
                <div className={styles.tileAction}>Click to view images →</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
